import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Heading, HStack, IconButton, List, ListItem, Select, Spinner, Text, useToast } from "@chakra-ui/react";
import { AddIcon, ArrowBackIcon, ChevronLeftIcon, ChevronRightIcon, RepeatIcon } from "@chakra-ui/icons";
import { Link as RouterLink, useParams, useNavigate } from "react-router-dom";
import rbacService from "../services/rbacService";
import type { Menu, Role, Application } from "../../rbac/types";

const RoleDetailPage: React.FC = () => {
  const { id } = useParams();
  const roleId = id as string; // UUID
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<Role | null>(null);
  const [apps, setApps] = useState<Application[]>([]);
  const [allMenus, setAllMenus] = useState<Menu[]>([]);
  const [assignedMenus, setAssignedMenus] = useState<Menu[]>([]);
  const [selectedAvailable, setSelectedAvailable] = useState<string | null>(null);
  const [selectedAssigned, setSelectedAssigned] = useState<string | null>(null);

  const availableMenus = useMemo(() => {
    const assignedIds = new Set(assignedMenus.map(m => m.id));
    // If role is app-scoped, filter menus by that app
    const scopeAppId = role?.app_id ?? null;
    return allMenus.filter(m => !assignedIds.has(m.id) && (scopeAppId == null || m.app_id === scopeAppId));
  }, [allMenus, assignedMenus, role?.app_id]);

  const load = async () => {
    setLoading(true);
    try {
      const [r, menus, assigned, applications] = await Promise.all([
        rbacService.getRoleById(roleId),
        rbacService.getMenus({ skip: 0, limit: 1000 }),
        rbacService.getRoleMenus(roleId),
        rbacService.getApplications({ skip: 0, limit: 1000 }),
      ]);
      setRole(r);
      setAllMenus(menus);
      setAssignedMenus(assigned);
      setApps(applications);
    } catch (e: any) {
      toast({ status: "error", title: "Gagal memuat detail role", description: e?.response?.data?.detail || e?.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [roleId]);

  const assign = async () => {
    if (selectedAvailable == null) return;
    try {
      await rbacService.assignMenuToRole(roleId, selectedAvailable);
      await load();
    } catch (e: any) {
      toast({ status: "error", title: "Gagal menambah menu", description: e?.response?.data?.detail || e?.message });
    }
  };

  const revoke = async () => {
    if (selectedAssigned == null) return;
    try {
      await rbacService.removeMenuFromRole(roleId, selectedAssigned);
      await load();
    } catch (e: any) {
      toast({ status: "error", title: "Gagal mencabut menu", description: e?.response?.data?.detail || e?.message });
    }
  };

  if (loading) return <Box p={6}><Spinner /></Box>;
  if (!role) return <Box p={6}>Role tidak ditemukan</Box>;

  return (
    <Box p={4}>
      <HStack justify="space-between" mb={4}>
        <HStack>
          <Button leftIcon={<ArrowBackIcon />} variant="ghost" onClick={() => navigate(-1)}>Back</Button>
          <Heading size="md">Role: {role.role_name}</Heading>
        </HStack>
        <IconButton aria-label="refresh" icon={<RepeatIcon />} onClick={() => load()} />
      </HStack>

      <Text mb={2}>App: {role.app_id == null ? "(Global)" : apps.find(a => a.id === role.app_id)?.app_name || role.app_id}</Text>

      <Flex gap={6}>
        <Box flex={1}>
          <Heading size="sm" mb={2}>Available Menus</Heading>
          <Box borderWidth="1px" borderRadius="md" p={2} maxH="400px" overflowY="auto">
            <List spacing={1}>
              {availableMenus.map(m => (
                <ListItem key={m.id} p={2} borderRadius="md" bg={selectedAvailable === m.id ? "green.50" : "transparent"} onClick={() => setSelectedAvailable(m.id)} cursor="pointer">
                  <Text fontSize="sm">[{m.app_id}] {m.display_name || m.menu_key} — {m.path}</Text>
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>

        <Flex direction="column" align="center" justify="center" gap={2}>
          <IconButton aria-label="assign" icon={<ChevronRightIcon />} onClick={assign} isDisabled={selectedAvailable == null} />
          <IconButton aria-label="revoke" icon={<ChevronLeftIcon />} onClick={revoke} isDisabled={selectedAssigned == null} />
        </Flex>

        <Box flex={1}>
          <Heading size="sm" mb={2}>Assigned Menus</Heading>
          <Box borderWidth="1px" borderRadius="md" p={2} maxH="400px" overflowY="auto">
            <List spacing={1}>
              {assignedMenus.map(m => (
                <ListItem key={m.id} p={2} borderRadius="md" bg={selectedAssigned === m.id ? "red.50" : "transparent"} onClick={() => setSelectedAssigned(m.id)} cursor="pointer">
                  <Text fontSize="sm">[{m.app_id}] {m.display_name || m.menu_key} — {m.path}</Text>
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
};

export default RoleDetailPage;
